import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceDot } from "recharts";
import { TrendingUp, MapPin } from "lucide-react";

export const AnalyticsCentre = () => {
  const { data, isLoading } = useQuery({
    queryKey: ["analytics-forecast"],
    queryFn: async () => (await api.get("/analytics/forecast")).data,
  });

  if (isLoading || !data) return <div className="text-[#B3C2CC]">Loading analytics…</div>;

  const combined = [
    ...data.revenue_history.map((h) => ({ ...h, kind: "actual" })),
    ...data.revenue_forecast.map((f) => ({ ...f, kind: "forecast" })),
  ];

  return (
    <div className="space-y-6" data-testid="page-analytics">
      <header>
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">National Analytics Centre</div>
        <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1 flex items-center gap-2">
          <TrendingUp className="text-[#C99A2E]"/> Predictive & Comparative Analytics
        </h1>
        <div className="text-[13px] text-[#B3C2CC] mt-1">Revenue forecasting · provincial comparison · investment hotspots.</div>
      </header>

      <div className="cad-panel p-5">
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Revenue trend + 3-month forecast (USD)</div>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={combined} margin={{ top: 20, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid stroke="#1e3245" strokeDasharray="3 3"/>
            <XAxis dataKey="month" stroke="#7C93A5" fontSize={11}/>
            <YAxis stroke="#7C93A5" fontSize={11} tickFormatter={(v) => `$${Math.round(v/1000)}k`}/>
            <Tooltip contentStyle={{ background: "#0B3558", border: "1px solid #C99A2E", color: "#F6F8FA" }}
                     formatter={(v) => `$${Math.round(v).toLocaleString()}`}/>
            <Line type="monotone" dataKey="amount" stroke="#C99A2E" strokeWidth={2.4} dot={{ r: 3 }}/>
          </LineChart>
        </ResponsiveContainer>
        <div className="text-[11px] font-mono-cad text-[#7C93A5] mt-2">
          Linear projection based on last 6 months of paid revenue. Forecast points shown after {data.revenue_history.at(-1)?.month || "—"}.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="cad-panel p-5">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E] flex items-center gap-1.5">
            <MapPin size={12}/> Investment hotspots (pending mining rights)
          </div>
          <div className="mt-3 space-y-2">
            {data.investment_hotspots.map((h, i) => (
              <div key={i} className="flex items-center justify-between text-[13px] border-b cad-hairline pb-1.5">
                <span className="text-[#F6F8FA]">{h.province}</span>
                <span className="font-mono-cad text-[#C99A2E]">{h.pending} pending</span>
              </div>
            ))}
          </div>
        </div>
        <div className="cad-panel p-5">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Provincial density</div>
          <div className="mt-3 space-y-1.5">
            {data.parcels_by_province.map((p, i) => {
              const mining = data.mining_by_province.find((m) => m._id === p.province)?.n || 0;
              const max = Math.max(...data.parcels_by_province.map((x) => x.n));
              return (
                <div key={i}>
                  <div className="flex items-center justify-between text-[12px]">
                    <span className="text-[#DCE3E8]">{p.province}</span>
                    <span className="font-mono-cad text-[#F6F8FA]">{p.n} parcels · {mining} mining</span>
                  </div>
                  <div className="w-full h-1.5 bg-[#18232D] rounded-full overflow-hidden">
                    <div className="h-full bg-[#C99A2E]" style={{ width: `${(p.n / max) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
